/**
 * CR-4060: Contract Edit Modal
 * Allows editing contract fields from Supplier Profile Contracts tab
 */
import { useState, useEffect, useRef } from 'react';
import { X, Loader2, Save, Upload, FileText, Trash2 } from 'lucide-react';
import { purchasingAPI, coreAPI, ContractPrice, UserProfile } from '../../lib/api';
import { toast } from 'sonner';

interface ContractEditModalProps {
  contract: ContractPrice;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function ContractEditModal({ contract, isOpen, onClose, onSaved }: ContractEditModalProps) {
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [removeFile, setRemoveFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // CR-5770: Contract Owner is the user on the customer (buyer) side who
    // owns the commercial relationship — not someone at the supplier. The
    // earlier wiring queried supplier users, which surfaced the wrong list
    // and made it effectively impossible for a customer admin to assign
    // themselves or a teammate.
    if (isOpen && contract.customer) {
      coreAPI.getCompanyUsers(contract.customer).then((res) => {
        setUsers(res.data);
      }).catch(() => {
        // Silently fail — dropdown will just be empty
      });
    }
  }, [isOpen, contract.customer]);

  const [form, setForm] = useState({
    contract_number: contract.contract_number || '',
    valid_to: contract.valid_to || '',
    auto_renew: contract.auto_renew || false,
    renewal_period_years: contract.renewal_period_years ?? '',
    notice_period_months: contract.notice_period_months ?? '',
    reminder_days: contract.reminder_days ?? '',
    risk_category: contract.risk_category || '',
    br_frequency_months: contract.br_frequency_months ?? '',
    owner: contract.owner ?? '',
    contract_negotiation: contract.contract_negotiation || false,
    notes: contract.notes || '',
  });

  useEffect(() => {
    setForm({
      contract_number: contract.contract_number || '',
      valid_to: contract.valid_to || '',
      auto_renew: contract.auto_renew || false,
      renewal_period_years: contract.renewal_period_years ?? '',
      notice_period_months: contract.notice_period_months ?? '',
      reminder_days: contract.reminder_days ?? '',
      risk_category: contract.risk_category || '',
      br_frequency_months: contract.br_frequency_months ?? '',
      owner: contract.owner ?? '',
      contract_negotiation: contract.contract_negotiation || false,
      notes: contract.notes || '',
    });
  }, [contract]);

  if (!isOpen) return null;

  const handleSave = async () => {
    try {
      setSaving(true);

      const renewalYears = form.auto_renew ? form.renewal_period_years : '';

      if (contractFile || removeFile) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append('contract_number', form.contract_number);
        if (form.valid_to) formData.append('valid_to', form.valid_to);
        formData.append('auto_renew', String(form.auto_renew));
        if (renewalYears !== '') formData.append('renewal_period_years', String(renewalYears));
        if (form.notice_period_months !== '') formData.append('notice_period_months', String(form.notice_period_months));
        if (form.reminder_days !== '') formData.append('reminder_days', String(form.reminder_days));
        formData.append('risk_category', form.risk_category);
        if (form.br_frequency_months !== '') formData.append('br_frequency_months', String(form.br_frequency_months));
        if (form.owner !== '') formData.append('owner', String(form.owner));
        formData.append('contract_negotiation', String(form.contract_negotiation));
        formData.append('notes', form.notes);

        if (contractFile) {
          formData.append('contract_file', contractFile);
        } else if (removeFile) {
          formData.append('contract_file', '');
        }

        await purchasingAPI.updateContractPriceFormData(contract.id, formData);
      } else {
        const payload: Record<string, unknown> = {
          contract_number: form.contract_number,
          valid_to: form.valid_to || null,
          auto_renew: form.auto_renew,
          renewal_period_years: renewalYears !== '' ? Number(renewalYears) : null,
          notice_period_months: form.notice_period_months !== '' ? Number(form.notice_period_months) : null,
          reminder_days: form.reminder_days !== '' ? Number(form.reminder_days) : null,
          risk_category: form.risk_category,
          br_frequency_months: form.br_frequency_months !== '' ? Number(form.br_frequency_months) : null,
          owner: form.owner !== '' ? Number(form.owner) : null,
          contract_negotiation: form.contract_negotiation,
          notes: form.notes,
        };
        await purchasingAPI.updateContractPrice(contract.id, payload as Partial<ContractPrice>);
      }

      toast.success('Contract updated');
      onSaved();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string; non_field_errors?: string[] } } };
      const message = error.response?.data?.detail
        || error.response?.data?.non_field_errors?.[0]
        || 'Failed to update contract';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
          <h2 className="text-lg font-semibold">Edit Contract</h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1">Contract Number</label>
            <input
              type="text"
              value={form.contract_number}
              onChange={(e) => setForm({ ...form, contract_number: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Expiry Date</label>
            <input
              type="date"
              value={form.valid_to}
              onChange={(e) => setForm({ ...form, valid_to: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Contract File</label>
            {contract.contract_file && !removeFile && !contractFile ? (
              <div className="flex items-center gap-2 border rounded-lg px-3 py-2 text-sm">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <a
                  href={contract.contract_file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline truncate flex-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {contract.contract_file.split('/').pop()}
                </a>
                <button
                  type="button"
                  onClick={() => setRemoveFile(true)}
                  className="text-red-500 hover:text-red-700 p-1"
                  title="Remove file"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setContractFile(file);
                    setRemoveFile(false);
                  }}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 border rounded-lg px-3 py-2 text-sm hover:bg-muted w-full"
                >
                  <Upload className="h-4 w-4" />
                  <span className="truncate">
                    {contractFile ? contractFile.name : 'Click to upload (PDF, DOC, XLS)'}
                  </span>
                </button>
                {(contractFile || removeFile) && (
                  <button
                    type="button"
                    onClick={() => {
                      setContractFile(null);
                      setRemoveFile(false);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="text-muted-foreground hover:text-foreground p-1"
                    title="Cancel"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">Auto-Renew</label>
              <select
                value={form.auto_renew ? 'yes' : 'no'}
                onChange={(e) => {
                  const enabled = e.target.value === 'yes';
                  setForm({
                    ...form,
                    auto_renew: enabled,
                    renewal_period_years: enabled ? form.renewal_period_years : '',
                  });
                }}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">
                Renewal Period (years)
                {!form.auto_renew && (
                  <span className="text-muted-foreground font-normal ml-1">— enable Auto-Renew to edit</span>
                )}
              </label>
              <input
                type="number"
                min="0"
                value={form.auto_renew ? form.renewal_period_years : ''}
                onChange={(e) => setForm({ ...form, renewal_period_years: e.target.value })}
                disabled={!form.auto_renew}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                placeholder={form.auto_renew ? 'e.g. 1' : '—'}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">Notice Period (months)</label>
              <input
                type="number"
                min="0"
                value={form.notice_period_months}
                onChange={(e) => setForm({ ...form, notice_period_months: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. 3"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Reminder (days before notice)</label>
              <input
                type="number"
                min="0"
                value={form.reminder_days}
                onChange={(e) => setForm({ ...form, reminder_days: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. 30"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">Risk Category</label>
              <select
                value={form.risk_category}
                onChange={(e) => setForm({ ...form, risk_category: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">-- Select --</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">BR Frequency (months)</label>
              <input
                type="number"
                min="0"
                value={form.br_frequency_months}
                onChange={(e) => setForm({ ...form, br_frequency_months: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. 6"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">Contract Negotiation</label>
              <select
                value={form.contract_negotiation ? 'true' : 'false'}
                onChange={(e) => setForm({ ...form, contract_negotiation: e.target.value === 'true' })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Contract Owner</label>
              <select
                value={form.owner}
                onChange={(e) => setForm({ ...form, owner: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">-- Not Assigned --</option>
                {users.map((u) => (
                  <option key={u.user.id} value={u.user.id}>
                    {u.user.full_name || `${u.user.first_name} ${u.user.last_name}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background border-t px-6 py-4 flex justify-end gap-3 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-muted text-sm font-medium"
          >
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
